import React from "react";
import { useNavigate } from "react-router-dom";

// MUI Imports
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// Project Imports
import Page from "../../../../../Components/Dashboard/Page";
import { fShortenNumber } from "../../../../../Utils/formatNumber";
import Iconify from "../../../../../Components/Dashboard/Iconify";

const RootStyle = styled(Card)(({ theme, bgcolor = "#FFE2E5" }) => ({
  boxShadow: "none",
  textAlign: "center",
  padding: theme.spacing(3, 0),
  color: theme.palette.primary.darker,
  backgroundColor: bgcolor,
  borderRadius: "16px",
}));

const IconWrapperStyle = styled("div")(({ theme, bgcolor = "#FA5A7D" }) => ({
  margin: "auto",
  display: "flex",
  borderRadius: "50%",
  alignItems: "center",
  width: theme.spacing(5),
  height: theme.spacing(5),
  justifyContent: "center",
  marginBottom: theme.spacing(2),
  color: "#fff",
  backgroundColor: bgcolor,
}));

const DataCard = ({ value, title, bgcolor, iconColor, theIcon, id }) => {
  return (
    <RootStyle bgcolor={bgcolor} id={id}>
      <IconWrapperStyle bgcolor={iconColor}>
        <Iconify icon={theIcon} width={22} height={22} />
      </IconWrapperStyle>
      <Typography variant="h5" fontWeight={700} fontSize={"25px"}>
        {fShortenNumber(value)}
      </Typography>
      <Typography variant="subtitle2" sx={{ opacity: 0.72, fontSize: "12px" }}>
        {title}
      </Typography>
    </RootStyle>
  );
};

const FormCard = ({ form, clickHandler }) => (
  <Grid
    item
    xs
    style={{
      display: "flex",
      flexDirection: "column",
      borderRadius: "8px",
      padding: "16px",
      rowGap: "16px",
      height: "fit-content",
      border: "1px solid #E5E7EB",
      boxShadow: "0 1px 8px 0px #343C440D",
      "&:hover": {
        border: "1px solid #1AB759",
      },
    }}
  >
    <Grid item xs>
      <IconWrapperStyle
        style={{ margin: 0, color: form.iconColor }}
        bgcolor={form.bgColor}
      >
        <Iconify icon={form.iconType} width={18} height={18} />
      </IconWrapperStyle>
    </Grid>
    <Grid>
      <Typography style={{ fontWeight: 600, color: "#212B36" }}>
        {form.name}
      </Typography>
      <Typography
        style={{ fontWeight: 400, color: "#52575C", fontSize: "12px" }}
      >
        {form.desc}
      </Typography>
    </Grid>
    <Grid style={{ display: "flex", justifyContent: "flex-end" }}>
      <Button
        variant="text"
        size="small"
        endIcon={<ArrowForwardIosIcon fontSize="small" />}
        style={{
          textTransform: "none",
        }}
        onClick={() => clickHandler(form.url)}
      >
        Fill Report
      </Button>
    </Grid>
  </Grid>
);

const MonitoringAndEvaluation = () => {
  const navigate = useNavigate();

  const cards = [
    {
      title: "Filled",
      value: 0,
      icon: "null",
      bgcolor: "#C8E7FD",
      iconColor: "#0095FF",
      theIcon: "fluent:people-team-16-filled",
      id: "total-filled",
    },
    {
      title: "Reminders",
      value: 1,
      icon: "null",
      bgcolor: "#ECE9CB",
      iconColor: "#BFAF25",
      theIcon: "fluent:people-team-delete-32-filled",
      id: "reminders",
    },
    // {
    //   title: "Total Fully Approved",
    //   value: dataReport?.totalPhysicalVerificationApproved,
    //   icon: "null",
    //   bgcolor: "#CBE1D7",
    //   iconColor: "#016C34",
    //   theIcon: "fluent:people-checkmark-16-filled",
    //   id: "active-partners",
    // },
    {
      title: "Total Pending",
      value: 3,
      icon: "null",
      bgcolor: "#FFE2E5",
      iconColor: "#FA5A7D",
      theIcon: "fluent:people-checkmark-16-filled",
      id: "total-pending",
    },
  ];

  const formsData = [
    {
      name: "Quarterly Post Camp Monitoring",
      desc: "A detailed quarterly review of SAED activities post camping covering manpower, corp members, and support. ",
      url: "/home/monitoringandevaluation/postCampMonitoring",
      iconType: "f7:doc-text",
      bgColor: "#EBF1FFCC",
      iconColor: "#2445CD",
    },
    {
      name: "Grant Beneficiaries Monitoring",
      desc: "A detailed review of beneficiaries of grant post camping covering amount, beneficiaries, and support given. ",
      url: "/home/monitoringandevaluation/grantBenForm",
      iconType: "eva:pantone-outline",
      bgColor: "#EBFFEFCC",
      iconColor: "#1AB759",
    },
    {
      name: "Orientation Monitoring",
      desc: "A detailed review of orientation activities during camping covering costs, corporations support, and events. ",
      url: "/home/monitoringandevaluation/orientationForm",
      iconType: "iconoir:weight",
      bgColor: "#EDEBFFCC",
      iconColor: "#5028C6",
    },
  ];

  function handleFormClick(url) {
    navigate(url);
  }

  return (
    <Page title="SAED | Monitoring and Evaluation">
      <Container maxWidth="xl">
        <Box
          sx={{
            pb: 2,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Grid
            item
            xs
            style={{
              display: "flex",
              flexGrow: 4,
            }}
          >
            <Typography
              variant="h5"
              style={{ fontWeight: 600, color: "#212B36" }}
            >
              Monitoring and Evaluation
            </Typography>
          </Grid>
        </Box>

        <Box sx={{ pb: 2 }}>
          <Grid container spacing={3}>
            {cards.map((card) => {
              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md
                  key={`${card.title}-${card.value}`}
                >
                  <DataCard
                    value={card.value}
                    title={card.title}
                    bgcolor={card.bgcolor}
                    iconColor={card.iconColor}
                    theIcon={card.theIcon}
                    id={card.id}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box>
              <Paper
                sx={{
                  boxShadow: "0 1px 2px 0 rgb(145 158 171 / 24%)",
                  borderRadius: "20px",
                }}
              >
                <Card
                  elevation={0}
                  sx={{ padding: "16px 0", borderRadius: "20px" }}
                >
                  <CardContent sx={{ padding: "0px !important" }}>
                    <Typography
                      variant="h6"
                      style={{
                        fontWeight: 600,
                        color: "#212B36",
                        paddingLeft: "16px",
                      }}
                    >
                      {/* Monitoring & Evaluation Forms */}
                      Monitoring & Evaluation Forms
                    </Typography>
                    <Grid
                      container
                      style={{
                        padding: "16px",
                        marginTop: "8px",
                        display: "flex",
                        flexDirection: "row",
                        gap: "16px",
                      }}
                    >
                      {formsData.map((form) => (
                        <FormCard form={form} clickHandler={handleFormClick} />
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export default MonitoringAndEvaluation;
